import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  History, 
  Play, 
  Trash2, 
  Car, 
  Gauge, 
  Clock, 
  FileVideo,
  ChevronRight,
  AlertCircle,
  RefreshCw,
  TrendingUp,
  Zap,
  Download
} from 'lucide-react';
import { apiService } from '../services/api';
import { HistoryItem, HistoryStats } from '../types';

export function VideoHistory() {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [stats, setStats] = useState<HistoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [historyRes, statsRes] = await Promise.all([
        apiService.getHistory(50),
        apiService.getHistoryStats()
      ]);

      if (historyRes.success && historyRes.data) {
        setHistory(historyRes.data.items);
      } else {
        setError(historyRes.error || 'Error al cargar historial');
      }

      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (err) {
      setError('Error de conexión con el servidor');
      console.error('Error loading history:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (jobId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este vídeo del historial?')) {
      return;
    }

    setDeleting(jobId);
    try {
      const response = await apiService.deleteFromHistory(jobId);
      if (response.success) {
        setHistory(history.filter(h => h.job_id !== jobId));
        // Reload stats
        const statsRes = await apiService.getHistoryStats();
        if (statsRes.success && statsRes.data) {
          setStats(statsRes.data);
        }
      } else {
        alert('Error al eliminar: ' + response.error);
      }
    } catch (err) {
      alert('Error al eliminar el vídeo');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
          <p className="text-dark-400">Cargando historial...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-red-400">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-dark-800 hover:bg-dark-700 text-dark-200 rounded-lg transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center">
                <FileVideo className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-dark-100">{stats.total_videos}</p>
                <p className="text-xs text-dark-500">Vídeos procesados</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-dark-100">{stats.total_vehicles_detected}</p>
                <p className="text-xs text-dark-500">Vehículos detectados</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Gauge className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-dark-100">{stats.overall_avg_speed.toFixed(1)}</p>
                <p className="text-xs text-dark-500">Velocidad media (km/h)</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-dark-100">{stats.overall_max_speed.toFixed(1)}</p>
                <p className="text-xs text-dark-500">Velocidad máxima (km/h)</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* History List */}
      <div className="glass rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-dark-800/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
              <History className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-dark-100">Historial de Vídeos</h2>
              <p className="text-xs text-dark-500">{history.length} vídeos procesados</p>
            </div>
          </div>
          <button
            onClick={loadData}
            className="p-2 hover:bg-dark-800 rounded-lg transition-colors"
            title="Actualizar"
          >
            <RefreshCw className="w-5 h-5 text-dark-400" />
          </button>
        </div>

        {history.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-dark-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileVideo className="w-8 h-8 text-dark-500" />
            </div>
            <p className="text-dark-400">No hay vídeos procesados aún</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              <Zap className="w-4 h-4" />
              Procesar un vídeo
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-dark-800/50">
            <AnimatePresence>
              {history.map((item, index) => (
                <motion.div
                  key={item.job_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 hover:bg-dark-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Thumbnail / Icon */}
                    <div className="w-16 h-16 bg-dark-800 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {item.processed_video_url ? (
                        <Play className="w-6 h-6 text-primary-400" />
                      ) : (
                        <FileVideo className="w-6 h-6 text-dark-500" />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-dark-100 truncate">
                        {item.filename || `Video ${item.job_id}`}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-dark-400">
                        <span className="flex items-center gap-1">
                          <Car className="w-3 h-3" />
                          {item.total_vehicles} vehículos
                        </span>
                        <span className="flex items-center gap-1">
                          <Gauge className="w-3 h-3" />
                          {item.avg_speed.toFixed(1)} km/h media
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDuration(item.video_duration)}
                        </span>
                      </div>
                      <p className="text-xs text-dark-500 mt-1">
                        {formatDate(item.created_at)}
                      </p>
                    </div>

                    {/* Status */}
                    <div className="flex-shrink-0">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.status === 'completed'
                          ? 'bg-green-500/20 text-green-400'
                          : item.status === 'error'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {item.status === 'completed' ? 'Completado' : 
                         item.status === 'error' ? 'Error' : 'Procesando'}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Download button */}
                      {item.processed_video_url && (
                        <a
                          href={apiService.getDownloadUrl(item.job_id)}
                          className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                          title="Descargar vídeo analizado"
                          download
                        >
                          <Download className="w-5 h-5" />
                        </a>
                      )}
                      <Link
                        to={`/history/${item.job_id}`}
                        className="p-2 bg-primary-500/20 hover:bg-primary-500/30 text-primary-400 rounded-lg transition-colors"
                        title="Ver detalle"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                      <button
                        onClick={() => handleDelete(item.job_id)}
                        disabled={deleting === item.job_id}
                        className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors disabled:opacity-50"
                        title="Eliminar"
                      >
                        {deleting === item.job_id ? (
                          <div className="w-5 h-5 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}

export default VideoHistory;
